import {
  Container,
  ContentLayout,
  Header,
  SpaceBetween,
  Toggle,
} from '@cloudscape-design/components';
import { uploadPartFromFirestore } from '@parallel-fluidics/shared/models/parts';
import PartsList from 'components/PartsList';
import useSortParts from 'components/PartsList/useSortParts';
import OrganizationSelect from 'features/organization/components/organizationSelect';
import useGetUserById from 'features/user/hooks/useGetUserById';
import {
  collection,
  doc,
  getFirestore,
  or,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { OrderList } from '../../components/Orders';
import OrderDetailProvider from '../../components/Orders/orderDetailProvider';
import PasswordReset from '../../components/PasswordReset';
import useRealtimeDocs from '../../hooks/use-realtimeDocs';

function User() {
  const { userID } = useParams();
  const { partsSort, setPartsSort } = useSortParts();
  const db = getFirestore();
  const {
    data: userData, isFetching, error, refetch,
  } = useGetUserById(userID);
  const [userOrdersOnly, setUserOrdersOnly] = useState(false);

  const userPartsQuery = useMemo(() => query(collection(db, 'parts'), where('hasAccessUsers', 'array-contains', userID), orderBy(partsSort.field, partsSort.isDescending ? 'desc' : 'asc')), [db, userID, partsSort]);
  const ordersQuery = useMemo(() => {
    if (!userData || !userData?.id) return null;
    if (userData.organization && !userOrdersOnly) {
      return query(
        collection(db, 'orders'),
        or(
          where('organization', '==', userData.organization),
          where('uid', '==', userData.id),
        ),
        orderBy('orderDate', 'desc'),
      );
    }
    return query(
      collection(db, 'orders'),
      where('uid', '==', userData.id),
      orderBy('orderDate', 'desc'),
    );
  }, [db, userData, userOrdersOnly]);

  const { data: orders, status: ordersStatus } = useRealtimeDocs({ query: ordersQuery });
  const { data: parts, status: partsStatus } = useRealtimeDocs({
    query: userPartsQuery,
    transformData: uploadPartFromFirestore,
  });

  const handleUserOrgChange = useCallback(async (org, isManualChange) => {
    if (!isManualChange) return;
    // update user's organization
    await updateDoc(doc(getFirestore(), 'users', userData.id), { organization: org.id });
    refetch();
  }, [userData?.id, refetch]);

  if (isFetching) {
    return <span>{`${userID} (loading email...)`}</span>;
  }

  if (error) {
    return <p>{error.message}</p>;
  }

  return (
    <ContentLayout
      header={(
        <Header
          variant="h1"
          actions={<PasswordReset userEmail={userData.email} />}
        >
          <span>{userData.email}</span>
        </Header>
    )}
    >
      <SpaceBetween size="m">
        {/* Organization */}
        <Container
          header={<Header variant="h2">Organization</Header>}
        >
          <OrganizationSelect
            defaultDomain={userData.email.split('@')[1]}
            label={`Select an Organization for ${userData.email}`}
            selectedOrgId={userData.organization}
            onOrgChange={handleUserOrgChange}
          />
        </Container>

        <PartsList
          header={<Header>Parts</Header>}
          parts={parts}
          isLoading={partsStatus === 'loading'}
          sorting={partsSort}
          onSortingChange={setPartsSort}
        />

        {ordersQuery && (
          <OrderDetailProvider>
            <OrderList
              header={(
                <Header
                  actions={(
                    userData?.organization && (
                      <Toggle
                        onChange={({ detail }) => setUserOrdersOnly(detail.checked)}
                        checked={userOrdersOnly}
                      >
                        This user’s orders only
                      </Toggle>
                    )
                  )}
                >
                  Orders
                </Header>
              )}
              orders={orders}
              isLoading={ordersStatus === 'loading'}
            />
          </OrderDetailProvider>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}

export default User;
